<template>
  <section class="custom-format">
    <div class="image-frame">
      <div
        ref="overlay"
        class="image-frame__overlay">
        <div class="image-frame__overlay-inner" />
      </div>
      <img
        ref="targetImage"
        :src="sourceImage"
        :style="{
          width: `${image.scale}px`,
          left: `${image.horizPos}px`,
          bottom: `${image.vertPos}px`
        }"
        class="source-image">
    </div>

    <div class="controls">
      <div class="scale">
        <label class="scale__label">
          scale
        </label>
        <div class="range-control">
          <v-btn
            icon
            small
            class="range-control__decrease"
            @click="image.scale -= rangeStep">
            <v-icon>remove</v-icon>
          </v-btn>
          <v-slider
            v-model="image.scale"
            :step="rangeStep"
            min="100"
            max="1000"
            ticks
            hide-details
            class="range-control__slider scale-slider" />
          <v-btn
            icon
            small
            class="range-control__increase"
            @click="image.scale += rangeStep">
            <v-icon>add</v-icon>
          </v-btn>
        </div>
      </div>
      <div class="vertical-position">
        <label class="vertical-position__label">
          vertical postion
        </label>
        <div class="range-control">
          <v-btn
            icon
            small
            class="range-control__decrease"
            @click="image.vertPos -= rangeStep">
            <v-icon>chevron_left</v-icon>
          </v-btn>
          <v-slider
            v-model="image.vertPos"
            :step="rangeStep"
            min="-200"
            max="200"
            ticks
            hide-details
            class="range-control__slider" />
          <v-btn
            icon
            small
            class="range-control__increase vertical-position-slider"
            @click="image.vertPos += rangeStep">
            <v-icon>chevron_right</v-icon>
          </v-btn>
        </div>
      </div>
      <div class="horizontal-position">
        <label class="horizontal-position__label">horizontal position</label>
        <div class="range-control">
          <v-btn
            icon
            small
            class="range-control__decrease"
            @click="image.horizPos -= rangeStep">
            <v-icon>chevron_left</v-icon>
          </v-btn>
          <v-slider
            v-model="image.horizPos"
            :step="rangeStep"
            min="-200"
            max="200"
            ticks
            hide-details
            class="range-control__slider horizontal-position-slider" />
          <v-btn
            icon
            small
            class="range-control__increase"
            @click="image.horizPos += rangeStep">
            <v-icon>chevron_right</v-icon>
          </v-btn>
        </div>
      </div>
      <v-btn
        icon
        class="reset-button"
        @click="reset">
        <v-icon class="reset-button__icon">refresh</v-icon>
      </v-btn>

      <!-- NEXT BUTTON FOR MOBILE LANDSCAPE -->
      <v-btn
        flat
        outline
        small
        color="primary"
        class="landscape-mode-next-button"
        @click="$emit('landscape-next')">
        next
      </v-btn>

      <canvas
        ref="customCanvas"
        class="custom-canvas" />
    </div>
  </section>
</template>

<script>
export default {
  name: 'PatchUpload',
  props: ['sourceImage'],
  data() {
    return {
      rangeStep: 5,
      image: {
        scale: 0,
        vertPos: 0,
        horizPos: 0,
      },
    };
  },
  mounted() {
    this.image.scale = this.$refs.overlay.clientWidth;
  },
  methods: {
    reset() {
      this.image.scale = this.$refs.overlay.clientWidth;
      this.image.vertPos = 0;
      this.image.horizPos = 0;
    },
    createCanvas() {
      const canvas = this.$refs.customCanvas;
      const { overlay } = this.$refs;
      const canvasWidth = 1200;
      const canvasHeight = 600;
      canvas.width = canvasWidth;
      canvas.height = canvasHeight;

      const ctx = canvas.getContext('2d');

      // white background
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvasWidth, canvasHeight);

      // image parameters
      const img = this.$refs.targetImage;

      const sx = img.offsetLeft - overlay.offsetLeft;
      const sy = img.offsetTop - overlay.offsetTop;

      const sWidth = overlay.clientWidth * (img.clientWidth / overlay.clientWidth);
      const sHeight = overlay.clientHeight * (img.clientHeight / overlay.clientHeight);

      const wR = canvasWidth / overlay.clientWidth;
      const hR = canvasHeight / overlay.clientHeight;

      // draw image to canvas
      ctx.drawImage(img, sx * wR, sy * hR, sWidth * wR, sHeight * hR);
      ctx.imageSmoothingEnabled = true;

      // encode to base 64
      const base64 = canvas.toDataURL();

      // emit data URL
      this.$emit('canvas-output', base64);
    },
  },
};
</script>

<style lang="stylus" scoped>
@require('../../stylus/base')

.custom-format
  display: flex
  flex-flow: column nowrap
  height: 100%
  width: 100%

.source-image
  position: relative
  display: inline-block
  transition: $base-transition

.custom-canvas
  display: none;
  visibility: hidden;

.image-frame
  position: relative
  display: flex
  align-items: center
  justify-content: center
  height: 100%
  max-height: 100vh
  width: 100%
  background-image: url('../../assets/images/grid-bg.svg');
  background-position: center center
  background-repeat: repeat repeat
  overflow: hidden
  border-radius: 0 0 $base-border-radius $base-border-radius
  @media screen and (max-width: 48rem) and (orientation: portrait)
    max-height: 50vh

  &__overlay
    position: absolute
    z-index: 2
    width: 350px
    height: 175px
    transition: $base-transition
    &::before,
    &::after
      content: ''
      position: absolute
      width: 1000px
      height: 175px
      background-color: rgba(6,9,15, .65)
      transition: $base-transition
    &::before
      left: -1000px
    &::after
      right: -1000px

  &__overlay-inner
    position: absolute
    z-index: 1
    width: 350px
    height: 175px
    transition: $base-transition
    &::before,
    &::after
      content: ''
      position: absolute
      left: -700px
      width: 4000px
      height: 900px
      background-color: rgba(6,9,15, .65)
      transition: $base-transition
    &::before
      top: -900px
    &::after
      bottom: -900px

  // OVERLAY SIZING
  @media screen and (min-width: 450px)
    &__overlay,
    &__overlay-inner
      width: 400px
      height: 200px
    &__overlay
      &::before,
      &::after
        height: 200px
    &__overlay-inner
      &::before,
      &::after
        left: -800px
  @media screen and (max-width: 48rem) and (orientation: landscape)
    &__overlay,
    &__overlay-inner
      width: 600px
      height: 300px
    &__overlay
      &::before,
      &::after
        height: 300px
    &__overlay-inner
      &::before,
      &::after
        left: -1200px
  @media screen and (min-width: 1000px)
    &__overlay,
    &__overlay-inner
      width: 600px
      height: 300px
    &__overlay
      &::before,
      &::after
        height: 300px
    &__overlay-inner
      &::before,
      &::after
        left: -1200px
  @media screen and (min-width: 1800px)
    &__overlay,
    &__overlay-inner
      width: 1000px
      height: 500px
    &__overlay
      &::before,
      &::after
        height: 500px
    &__overlay-inner
      &::before,
      &::after
        left: -2000px

/*
 * FORMATTING CONTROLS:
 * Base Styles:
 * Mobile Portrait View
 */
.controls
  display: flex
  flex-flow: column nowrap
  align-items: center
  justify-content: center
  width: 100%

.scale,
.vertical-position,
.horizontal-position
  display: flex
  flex-flow: column nowrap
  align-items: center
  justify-content: center
  &__label
    font-size: $font-size--small
    font-weight: $base-header-font-weight
    text-transform: uppercase
    text-align: center
    color: $accent--dark

.range-control
  display: flex
  align-items: center
  justify-content: space-between
  &__decrease,
  &__increase
    margin: 0
    color: $primary
  &__slider
    margin: 0
    padding: 0
    height: auto

.reset-button
  margin: 0
  color: $primary

.landscape-mode-next-button
  display: none

/*
 * FORMATTING CONTROLS:
 * Mobile Landscape View
 */
@media screen and (max-width: 48rem) and (orientation: landscape)
  .custom-format
    z-index: 200
    position: absolute
    top: -3rem
    height: 100vh
    width: 100vw
  .image-frame
    position: absolute
    z-index: 0
    height: 100%
    width: 100%
    border-radius: 0
  .controls
    display: flex
    align-items: flex-start
    justify-content: flex-end
    z-index: 1
    height: 100%
    width: 100%

  .range-control
    opacity: .75

  .scale,
  .vertical-position,
  .horizontal-position
    align-items: flex-start
    width: 10rem
    &__label
      color: $accent
      opacity: .75
      text-shadow: $base-box-shadow

  .landscape-mode-next-button
    display: flex
    position: absolute
    bottom: 0
    right: 0

/*
 * FORMATTING CONTROLS:
 * Desktop View
 */
 @media screen and (min-width: 48rem)
   .custom-format
     display: flex
     flex-flow: row nowrap
     align-items: center
     justify-content: space-between

   .image-frame
     border-radius: $base-border-radius

   .controls
     max-width: 20rem

</style>
